import { useState, useCallback } from 'react';
import { SpotifyTrack } from '@/services/spotifyService';
import { enhancedSpotifyService, GenreSuggestion } from '@/services/enhancedSpotifyService';

export interface GenreCreationStatus {
  isCreating: boolean;
  created: number;
  found: number;
  total: number;
}

export interface UseSpotifyGenreDetectionOptions {
  /**
   * Automatically apply genres when detected
   * @default false
   */
  autoApply?: boolean;

  /**
   * Maximum number of genres to detect/apply
   * @default 6
   */
  maxGenres?: number;

  /**
   * Minimum confidence threshold for auto-applying genres
   * @default 0.6
   */
  minConfidence?: number;

  /**
   * Callback when genres are detected
   */
  onGenresDetected?: (genres: string[], suggestions: GenreSuggestion[]) => void;

  /**
   * Callback when genre creation status changes
   */
  onCreationStatusChange?: (status: GenreCreationStatus) => void;
}

export interface UseSpotifyGenreDetectionReturn {
  /** Genre suggestions from Spotify */
  suggestions: GenreSuggestion[];

  /** Whether currently detecting genres */
  isDetecting: boolean;

  /** Status of genre creation process */
  creationStatus: GenreCreationStatus;

  /** Detect genres for a Spotify track */
  detectGenres: (track: SpotifyTrack) => Promise<GenreSuggestion[]>;

  /** Apply selected genres (returns slug array) */
  applyGenres: (suggestions: GenreSuggestion[]) => string[];

  /** Clear current suggestions */
  clearSuggestions: () => void;

  /** Reset state */
  reset: () => void;
}

const initialCreationStatus: GenreCreationStatus = {
  isCreating: false,
  created: 0,
  found: 0,
  total: 0,
};

/**
 * Centralized hook for Spotify genre detection
 *
 * Provides consistent genre detection logic across all forms in the application.
 * Handles automatic genre detection, creation status tracking, and suggestion management.
 *
 * @example
 * ```tsx
 * const { suggestions, isDetecting, detectGenres } = useSpotifyGenreDetection({
 *   autoApply: true,
 *   maxGenres: 6,
 *   onGenresDetected: (genres) => form.setValue('genres', genres)
 * });
 *
 * // When Spotify track is selected
 * const handleTrackSelect = async (track: SpotifyTrack) => {
 *   await detectGenres(track);
 * };
 * ```
 */
export const useSpotifyGenreDetection = (
  options: UseSpotifyGenreDetectionOptions = {}
): UseSpotifyGenreDetectionReturn => {
  const {
    autoApply = false,
    maxGenres = 6,
    minConfidence = 0.6,
    onGenresDetected,
    onCreationStatusChange,
  } = options;

  const [suggestions, setSuggestions] = useState<GenreSuggestion[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [creationStatus, setCreationStatus] = useState<GenreCreationStatus>(initialCreationStatus);

  /**
   * Update creation status and notify callback
   */
  const updateCreationStatus = useCallback((status: GenreCreationStatus) => {
    setCreationStatus(status);
    onCreationStatusChange?.(status);
  }, [onCreationStatusChange]);

  /**
   * Intercept fetch calls to track genre creation
   */
  const interceptGenreCreation = useCallback((originalFetch: typeof window.fetch): typeof window.fetch => {
    return async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : args[0].url;

      if (url.includes('/api/genres/create-from-spotify')) {
        updateCreationStatus({ ...creationStatus, isCreating: true });

        const response = await originalFetch(...args);
        const clonedResponse = response.clone();

        try {
          const result = await clonedResponse.json();
          if (result.success) {
            updateCreationStatus({
              isCreating: false,
              created: result.totalCreated || 0,
              found: result.totalFound || 0,
              total: result.totalProcessed || 0,
            });
          }
        } catch (e) {
          // Ignore JSON parsing errors
          updateCreationStatus(initialCreationStatus);
        }

        return response;
      }

      return originalFetch(...args);
    };
  }, [creationStatus, updateCreationStatus]);

  /**
   * Detect genres for a Spotify track
   */
  const detectGenres = useCallback(async (track: SpotifyTrack): Promise<GenreSuggestion[]> => {
    setIsDetecting(true);
    updateCreationStatus(initialCreationStatus);

    // Store original fetch
    const originalFetch = window.fetch;
    let genreCreationRequested = false;

    try {
      // Intercept fetch to track genre creation
      window.fetch = interceptGenreCreation(originalFetch);

      const detectedSuggestions = await enhancedSpotifyService.getGenreSuggestions(track);

      // Restore original fetch
      window.fetch = originalFetch;

      setSuggestions(detectedSuggestions);

      // If no genre creation was triggered, reset status
      if (!genreCreationRequested) {
        updateCreationStatus(initialCreationStatus);
      }

      // Auto-apply if enabled
      if (autoApply && detectedSuggestions.length > 0) {
        const genreSlugs = applyGenres(detectedSuggestions);
        onGenresDetected?.(genreSlugs, detectedSuggestions);
      }

      return detectedSuggestions;
    } catch (error) {
      console.error('Error detecting genres:', error);
      window.fetch = originalFetch; // Restore on error
      updateCreationStatus(initialCreationStatus);
      setSuggestions([]);
      return [];
    } finally {
      setIsDetecting(false);
    }
  }, [autoApply, interceptGenreCreation, onGenresDetected, updateCreationStatus]);

  /**
   * Apply genres from suggestions
   * Returns array of genre slugs
   */
  const applyGenres = useCallback((suggestionsToApply: GenreSuggestion[]): string[] => {
    // Filter by confidence threshold and limit to maxGenres
    const filtered = suggestionsToApply
      .filter(s => s.confidence >= minConfidence)
      .slice(0, maxGenres);

    // Extract slugs (use slug if available, fallback to lowercase name)
    const slugs = filtered.map(s => s.slug || s.name.toLowerCase().replace(/\s+/g, '-'));

    return slugs;
  }, [maxGenres, minConfidence]);

  /**
   * Clear current suggestions
   */
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setSuggestions([]);
    setIsDetecting(false);
    updateCreationStatus(initialCreationStatus);
  }, [updateCreationStatus]);

  return {
    suggestions,
    isDetecting,
    creationStatus,
    detectGenres,
    applyGenres,
    clearSuggestions,
    reset,
  };
};
